import { z } from 'zod';

import { PhotoAngleSchema, TicketStatusSchema, VisitorTypeSchema } from './ticket-schemas.js';

// admin-analytics-expansion P3a (D6/D7/D8, spec ticket-detail, #2559) —
// single-ticket dispute aggregate contract. Shared by GetTicketDetailUseCase
// (backend) and getTicketDetail() (api-client). Read-only — no mutation
// fields. The audit timeline is DELIBERATELY NOT part of this contract (D7):
// the detail route makes a SEPARATE existing getTicketHistory(id) call.

// ── Media (presigned-GET, D8) ────────────────────────────────────────────────

export const TicketDetailPhotoSchema = z.object({
  id: z.string().uuid(),
  angle: PhotoAngleSchema,
  /** Short-TTL presigned GET url — NOT the raw storageKey (D8). */
  url: z.string().url(),
  expiresAt: z.string().datetime(),
});
export type TicketDetailPhoto = z.infer<typeof TicketDetailPhotoSchema>;

export const TicketDetailIncidentPhotoSchema = z.object({
  id: z.string().uuid(),
  url: z.string().url(),
  expiresAt: z.string().datetime(),
});
export type TicketDetailIncidentPhoto = z.infer<typeof TicketDetailIncidentPhotoSchema>;

// ── SMS messages ──────────────────────────────────────────────────────────────

export const TicketDetailSmsMessageSchema = z.object({
  id: z.string().uuid(),
  reference: z.string().min(1),
  to: z.string().min(1),
  body: z.string().min(1),
  locale: z.enum(['en', 'es']),
  status: z.enum(['queued', 'sent', 'failed']),
  provider: z.string().min(1),
  providerMessageId: z.string().nullable(),
  errorCode: z.string().nullable(),
  createdAt: z.string().datetime(),
});
export type TicketDetailSmsMessage = z.infer<typeof TicketDetailSmsMessageSchema>;

// ── Exits ─────────────────────────────────────────────────────────────────────

export const TicketDetailExitSchema = z.object({
  id: z.string().uuid(),
  sequence: z.number().int().positive(),
  exitAt: z.string().datetime(),
  reentryAt: z.string().datetime().nullable(),
  exitFeeCents: z.number().int().nonnegative(),
});
export type TicketDetailExit = z.infer<typeof TicketDetailExitSchema>;

// ── Membership coverage ───────────────────────────────────────────────────────
// Deliberately a lightweight projection (id/status/currentPeriodEnd) — NOT the
// full Membership aggregate. Kept as a plain string status (not an imported
// enum) to avoid a hard dependency of this contract on the memberships module.

export const TicketDetailMembershipSchema = z.object({
  id: z.string().uuid(),
  status: z.string(),
  currentPeriodEnd: z.string().datetime().nullable(),
});
export type TicketDetailMembership = z.infer<typeof TicketDetailMembershipSchema>;

// ── Payment breakdown (incl. delay-recharge settlement — confirmed default) ──

export const TicketDetailPaymentSchema = z.object({
  status: z.enum(['unpaid', 'paid']),
  method: z.enum(['stripe', 'cash', 'comp', 'membership']).nullable(),
  paymentIntentId: z.string().nullable(),
  paidAt: z.string().datetime().nullable(),
  amountPaidCents: z.number().int().nullable(),
  tipAmountCents: z.number().int().nullable(),
  // post-payment-delay-recharge S3a — SEPARATE settlement channel, included
  // per confirmed default (#2558): detail payload INCLUDES delay-recharge fields.
  delayRechargeIntentId: z.string().nullable(),
  delayRechargePaidAt: z.string().datetime().nullable(),
  delayRechargeAmountCents: z.number().int().nullable(),
});
export type TicketDetailPayment = z.infer<typeof TicketDetailPaymentSchema>;

// ── Full aggregate response ───────────────────────────────────────────────────

export const TicketDetailResponseSchema = z.object({
  id: z.string().uuid(),
  ticketNumber: z.number().int().positive(),
  locationId: z.string().uuid(),
  status: TicketStatusSchema,
  plate: z.string(),
  qrTag: z.string().nullable(),
  ticketType: z.enum(['sms', 'qr']),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  phone: z.string().nullable(),
  visitorEmail: z.string().nullable(),
  roomNumber: z.string().nullable(),
  visitorType: VisitorTypeSchema.nullable(),
  parkingZone: z.string().nullable(),
  valetNotes: z.string().nullable(),
  smsDispatched: z.boolean(),
  checkedInAt: z.string().datetime(),
  retrievalRequestedAt: z.string().datetime().nullable(),
  claimedAt: z.string().datetime().nullable(),
  deliveredAt: z.string().datetime().nullable(),
  retrievalDurationSeconds: z.number().int().nonnegative().nullable(),
  readyForPickupAt: z.string().datetime().nullable(),
  payment: TicketDetailPaymentSchema,
  membership: TicketDetailMembershipSchema.nullable(),
  photos: z.array(TicketDetailPhotoSchema),
  incidentPhotos: z.array(TicketDetailIncidentPhotoSchema),
  smsMessages: z.array(TicketDetailSmsMessageSchema),
  exits: z.array(TicketDetailExitSchema),
});
export type TicketDetailResponse = z.infer<typeof TicketDetailResponseSchema>;
